// Saved views registry: maps a stable view_key to its page route and display
// label. The view_key is the storage key for a page's saved filters
// (company_user_saved_filters.view_key) and must stay stable forever.
//
// A page joins the saved-views feature by adding an entry here plus wiring its
// filter state to useSavedFilters. The SavedViewsDrawer uses this registry to
// label sections and to navigate ("open" a view).
//
// Adding a page: pick a stable, namespaced key (area.page), give it the route
// the user lands on and a short label (first word capitalized only).

export interface SavedViewRegistryEntry {
  /** Stable storage key. Never change once shipped. */
  key: string
  /** Router path the drawer navigates to when opening a view for this key. */
  route: string
  /** Short human label for the drawer section (first word capitalized only). */
  label: string
  /** Optional tim-icons class for the drawer section header. */
  icon?: string
  /** Optional inline SVG markup, used instead of `icon` when a matching
   *  tim-icons glyph doesn't exist (e.g. the kanban columns icon). */
  iconSvg?: string
  /** Fixed query the page needs to show this view's list (e.g. a tab selector).
   *  Also tells this entry apart from a sibling that shares the same path. */
  query?: Record<string, string>
  /** Per-entity page (one board per project, one results page per survey). The
   *  saved blob's `_entity.id` fills `param`: a `:param` segment of `route` when
   *  `in` is 'path', or the `param` query key when `in` is 'query'. */
  entity?: { param: string; in: 'path' | 'query' }
  /** Where an entity view lands when its blob carries no `_entity` (rows saved
   *  before entity scoping existed). */
  listRoute?: string
}

/** Entity stored in a saved blob under `_entity` by FilterBar's `saved-filters-entity` prop. */
export interface SavedViewEntity {
  id: string | number
  name?: string
}

/** A resolved navigation target for opening a saved view. */
export interface SavedViewTarget {
  path: string
  query: Record<string, string>
}

// The kanban board icon has no tim-icons equivalent; reuse the exact columns SVG
// the bookings view-switcher toggle uses so the drawer section matches it.
const KANBAN_ICON_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" /></svg>'

// Order here is the drawer's fallback section order (the current page is always
// floated to the top at render time regardless of this order). Order mirrors the
// sidebar (Bookings -> Purchases -> Games -> Surveys -> Mail -> Customers ->
// Waivers -> Settings) so views are findable by the same layout users navigate by.
export const SAVED_VIEW_REGISTRY: SavedViewRegistryEntry[] = [
  { key: 'bookings.list', route: '/bookings/list', label: 'Bookings list', icon: 'tim-icons icon-bullet-list-67' },
  { key: 'bookings.calendar', route: '/bookings/calendar', label: 'Bookings calendar', icon: 'tim-icons icon-calendar-60' },
  { key: 'bookings.kanban', route: '/bookings/kanban', label: 'Kanban board', iconSvg: KANBAN_ICON_SVG },
  { key: 'booking-tickets', route: '/bookings/tickets', label: 'Booking tickets', icon: 'tim-icons icon-single-copy-04' },
  { key: 'booking-requests', route: '/bookings/requests', label: 'Booking requests', icon: 'tim-icons icon-paper' },
  { key: 'transactions', route: '/purchases/transactions', label: 'Transactions', icon: 'tim-icons icon-money-coins' },
  { key: 'payments', route: '/purchases/payments', label: 'Payments', icon: 'tim-icons icon-credit-card' },
  { key: 'carts', route: '/purchases/carts', label: 'Carts', icon: 'tim-icons icon-cart' },
  { key: 'gift-cards', route: '/purchases/gift-cards', label: 'Gift cards', icon: 'tim-icons icon-gift-2' },
  { key: 'inventory', route: '/purchases/inventory', label: 'Inventory', icon: 'tim-icons icon-cart' },
  { key: 'promo-codes', route: '/purchases/promos', label: 'Promo codes', icon: 'tim-icons icon-tag' },
  { key: 'game-settings', route: '/games/settings', label: 'Events', icon: 'tim-icons icon-shape-star' },
  { key: 'game-tracker', route: '/games/tracker/select', label: 'Event tracker', icon: 'tim-icons icon-controller' },
  { key: 'game-instances', route: '/games/history', label: 'Event history', icon: 'tim-icons icon-shape-star' },
  { key: 'checklists', route: '/checklists/new', label: 'Checklists', icon: 'tim-icons icon-bullet-list-67' },
  { key: 'checklist-history', route: '/checklists/history', label: 'Checklist history', icon: 'tim-icons icon-check-2' },
  { key: 'survey-history', route: '/surveys/results', label: 'Survey results', icon: 'tim-icons icon-chart-bar-32', entity: { param: 'id', in: 'query' }, listRoute: '/surveys/view' },
  { key: 'custom-emails', route: '/mail/custom/landing', label: 'Custom emails', icon: 'tim-icons icon-email-85' },
  // SMS templates are a tab on the custom emails landing page; the fixed tab query
  // both opens that tab and distinguishes this entry from custom-emails above.
  { key: 'sms-templates', route: '/mail/custom/landing', query: { tab: '2' }, label: 'SMS templates', icon: 'tim-icons icon-chat-33' },
  { key: 'campaigns', route: '/marketing/campaigns', label: 'Campaigns', icon: 'tim-icons icon-send' },
  // Messages: one key per tab, shared by its by-customer list and its per-message log
  // (the Every message toggle in the saved view picks which one shows).
  { key: 'email-messages', route: '/mail/messages', query: { tab: 'email' }, label: 'Email messages', icon: 'tim-icons icon-email-85' },
  { key: 'sms-messages', route: '/mail/messages', query: { tab: 'sms' }, label: 'SMS messages', icon: 'tim-icons icon-chat-33' },
  { key: 'players', route: '/customers/all', label: 'Customers', icon: 'tim-icons icon-single-02' },
  { key: 'waivers', route: '/waivers', label: 'Waivers', icon: 'tim-icons icon-paper' },
  // Photo overlays live under one photo settings template per group; the template id is the query id.
  { key: 'photo-overlays', route: '/photos/settings', label: 'Photo overlays', icon: 'tim-icons icon-camera-18', entity: { param: 'id', in: 'query' }, listRoute: '/photos/new' },
  { key: 'users', route: '/settings/user-management', label: 'Users', icon: 'tim-icons icon-single-02' },
  { key: 'roles', route: '/settings/roles', label: 'Roles', icon: 'tim-icons icon-badge' },
  { key: 'custom-fields', route: '/settings/custom-fields', label: 'Custom fields', icon: 'tim-icons icon-align-left-2' },
  { key: 'event-logs', route: '/settings/logs', label: 'Event logs', icon: 'tim-icons icon-notes' },
  { key: 'api-requests', route: '/settings/api-requests', label: 'API requests', icon: 'tim-icons icon-key-25' },
  { key: 'migration-logs', route: '/settings/migration', label: 'Migration logs', icon: 'tim-icons icon-cloud-upload-94' },
  { key: 'billing-invoices', route: '/settings/billing', label: 'Billing invoices', icon: 'tim-icons icon-paper' },
  { key: 'reports-list', route: '/reports', label: 'Reports', icon: 'tim-icons icon-chart-bar-32' },
  { key: 'roadmap', route: '/roadmap', label: 'Roadmap', icon: 'tim-icons icon-map-big' },
  { key: 'projects', route: '/projects', label: 'Projects', icon: 'tim-icons icon-app' },
  { key: 'project-tickets', route: '/projects/:id', label: 'Project tickets', icon: 'tim-icons icon-bullet-list-67', entity: { param: 'id', in: 'path' }, listRoute: '/projects' },
  { key: 'time-logs', route: '/time-tracker/logs', label: 'Time tracker', icon: 'tim-icons icon-watch-time' },
]

const REGISTRY_BY_KEY: Record<string, SavedViewRegistryEntry> = SAVED_VIEW_REGISTRY.reduce(
  (acc, entry) => { acc[entry.key] = entry; return acc },
  {} as Record<string, SavedViewRegistryEntry>,
)

export function getSavedViewEntry(viewKey: string): SavedViewRegistryEntry | null {
  return REGISTRY_BY_KEY[viewKey] || null
}

export function getSavedViewLabel(viewKey: string): string {
  return REGISTRY_BY_KEY[viewKey]?.label || viewKey
}

/** Read the entity a saved blob was scoped to, or null when it has none. */
export function getSavedViewEntity(filters: Record<string, any> | null | undefined): SavedViewEntity | null {
  const e = filters ? filters._entity : null
  if (!e || e.id == null || e.id === '') return null
  return { id: e.id, name: typeof e.name === 'string' && e.name !== '' ? e.name : undefined }
}

/**
 * Where to navigate to open a saved view. Fills the entity param from the blob for
 * per-entity entries; an entity entry whose blob has no `_entity` falls back to its
 * listRoute (null when it has none, meaning the view can't be opened from the drawer).
 */
export function buildSavedViewRoute(entry: SavedViewRegistryEntry, filters: Record<string, any> | null | undefined): SavedViewTarget | null {
  const query: Record<string, string> = { ...(entry.query || {}) }
  if (!entry.entity) return { path: entry.route, query }
  const ent = getSavedViewEntity(filters)
  if (!ent) return entry.listRoute ? { path: entry.listRoute, query: {} } : null
  const id = String(ent.id)
  if (entry.entity.in === 'path') {
    return { path: entry.route.replace(':' + entry.entity.param, encodeURIComponent(id)), query }
  }
  query[entry.entity.param] = id
  return { path: entry.route, query }
}

function pathMatchesPattern(pattern: string, path: string): boolean {
  const a = pattern.split('/')
  const b = path.split('/')
  if (a.length !== b.length) return false
  return a.every((seg, i) => (seg.startsWith(':') ? b[i] !== '' : seg === b[i]))
}

function fixedQueryMatches(entry: SavedViewRegistryEntry, query: Record<string, any>): boolean {
  if (!entry.query) return true
  return Object.keys(entry.query).every((k) => String(query[k] == null ? '' : query[k]) === entry.query![k])
}

/**
 * The registry key for the page at `path` (+ `query`), or null. Entries with a fixed
 * query win over a sibling on the same path without one (SMS templates tab vs the
 * custom emails landing it lives on); `:param` segments match any non-empty value.
 */
export function matchSavedViewKey(path: string, query: Record<string, any> = {}): string | null {
  let fallback: string | null = null
  for (const e of SAVED_VIEW_REGISTRY) {
    if (!pathMatchesPattern(e.route, path)) continue
    if (e.query) {
      if (fixedQueryMatches(e, query)) return e.key
      continue
    }
    if (fallback == null) fallback = e.key
  }
  return fallback
}
